import type { EditorContext } from "../tools/EditorContext";
import {
  DEFAULT_VECTOR_EXPORT_SETTINGS,
  type ExportOperation,
  type StitchExportMode,
  type VectorExportSettings,
} from "../exporters/sceneToSvg";
import { downloadSvgOptimized } from "../exporters/svgExporter";
import { downloadDxf } from "../exporters/dxfExporter";
import { markPaidFeature } from "./paidPreview";

/**
 * "Optimized vector export" settings dialog (#572). Opens when the
 * `vector-export` flag is on and the user picks SVG or DXF in the Export
 * dialog — the basic (flag-off) path downloads one-click with defaults and
 * never sees this. Mirrors the advanced print dialog's structure (issue
 * #289): a native <dialog>, `data-field` selectors, fieldsets per concern.
 *
 * Sections:
 *  - Content — pattern / construction-line checkboxes and the stitching
 *    representation (working-area holes / all holes / seam lines / none).
 *  - Operations — which laser operation each content category maps to.
 *  - Laser settings — per-operation colors and hairline widths (#572
 *    defaults: red 0.01 mm cuts, blue 0.01 mm scores, black engraving at
 *    original thickness).
 *  - Calibration — optional 3 cm / 2 in size-check squares.
 *  - SVG only: the Cricut compatibility pass (unit remap, curve flatten,
 *    clip stripping — the old separate "SVG (Cricut)" button).
 */
export class VectorExportDialog {
  private host: HTMLElement;
  private ctx: EditorContext;
  private dialogEl: HTMLDialogElement | null = null;

  constructor(host: HTMLElement, ctx: EditorContext) {
    this.host = host;
    this.ctx = ctx;
  }

  open(format: "svg" | "dxf") {
    this.teardown();
    const d = DEFAULT_VECTOR_EXPORT_SETTINGS;

    // Shared inline styles, matching the advanced print dialog's fieldset /
    // label conventions (#289) so the two settings dialogs read as one family.
    const FIELDSET =
      'style="display:grid;gap:8px;align-content:start;border:1px solid rgba(127,127,127,0.4);border-radius:6px;padding:8px 10px;margin:0;"';
    const LEGEND = 'style="padding:0 4px;"';
    const CHECKROW = 'style="display:flex;gap:8px;align-items:center;"';
    const SELECTROW =
      'style="display:grid;grid-template-columns:1fr 1fr;gap:8px;align-items:center;"';

    const opSelect = (field: string, selected: ExportOperation) => {
      const opt = (v: ExportOperation, label: string) =>
        `<option value="${v}"${v === selected ? " selected" : ""}>${label}</option>`;
      return `<select data-field="${field}">
        ${opt("cut", "Cut")}
        ${opt("score", "Etch / score")}
        ${opt("engrave", "Engrave")}
      </select>`;
    };

    const modal = document.createElement("dialog");
    modal.className = "modal vector-export-modal";
    modal.innerHTML = `
      <header style="display:flex;align-items:center;justify-content:space-between;gap:12px;">
        <span>Export ${format.toUpperCase()} — settings</span>
        <button class="btn" type="button" data-act="close" aria-label="Close" title="Close" style="border:none;background:transparent;font-size:18px;line-height:1;padding:2px 8px;cursor:pointer;color:inherit;">✕</button>
      </header>
      <div class="body">
        <div class="vector-export-grid">
          <fieldset ${FIELDSET}>
            <legend ${LEGEND}>Content</legend>
            <label ${CHECKROW}>
              <input type="checkbox" data-field="pattern" checked />
              <span>Pattern (parts, holes, cutouts, decorations)</span>
            </label>
            <label ${CHECKROW}>
              <input type="checkbox" data-field="construction" checked />
              <span>Construction lines</span>
            </label>
            <label ${SELECTROW}>
              <span>Stitching</span>
              <select data-field="stitching">
                <option value="working-holes" selected>Holes (working area)</option>
                <option value="all-holes">Holes (full line)</option>
                <option value="seams">Seam lines only</option>
                <option value="off">None</option>
              </select>
            </label>
          </fieldset>
          <fieldset ${FIELDSET}>
            <legend ${LEGEND}>Operations</legend>
            <label ${SELECTROW}>
              <span>Pattern</span>
              ${opSelect("opPattern", d.operations.pattern)}
            </label>
            <label ${SELECTROW}>
              <span>Construction lines</span>
              ${opSelect("opConstruction", d.operations.constructionLines)}
            </label>
            <label ${SELECTROW}>
              <span>Stitching</span>
              ${opSelect("opStitching", d.operations.stitching)}
            </label>
          </fieldset>
          <fieldset ${FIELDSET}>
            <legend ${LEGEND}>Laser settings</legend>
            <label ${CHECKROW}>
              <span style="flex:1;">Cutting lines</span>
              <input type="color" data-field="cutColor" value="${d.laser.cutColor}" />
              <input type="number" data-field="cutWidth" value="${d.laser.cutWidthMm}" min="0.01" step="0.01" style="width:76px;" />
              <span>mm</span>
            </label>
            <label ${CHECKROW}>
              <span style="flex:1;">Inside cuts (holes &amp; cutouts)</span>
              <input type="color" data-field="insideCutColor" value="${d.laser.insideCutColor}" />
              <span style="opacity:0.7;">cut width</span>
            </label>
            <label ${CHECKROW}>
              <span style="flex:1;">Etching / scoring</span>
              <input type="color" data-field="scoreColor" value="${d.laser.scoreColor}" />
              <input type="number" data-field="scoreWidth" value="${d.laser.scoreWidthMm}" min="0.01" step="0.01" style="width:76px;" />
              <span>mm</span>
            </label>
            <label ${CHECKROW}>
              <span style="flex:1;">Raster engraving</span>
              <input type="color" data-field="engraveColor" value="${d.laser.engraveColor}" />
              <span style="opacity:0.7;">original thickness</span>
            </label>
          </fieldset>
          <div style="display:grid;gap:12px;">
            <fieldset ${FIELDSET}>
              <legend ${LEGEND}>Size calibration</legend>
              <label ${CHECKROW}>
                <input type="checkbox" data-field="calMetric" />
                <span>3 cm box (metric)</span>
              </label>
              <label ${CHECKROW}>
                <input type="checkbox" data-field="calImperial" />
                <span>2 in box (imperial)</span>
              </label>
            </fieldset>
            ${
              format === "svg"
                ? `<fieldset ${FIELDSET}>
              <legend ${LEGEND}>Compatibility</legend>
              <label ${CHECKROW}>
                <input type="checkbox" data-field="cricut" />
                <span>Cricut (remap coordinates to the active unit, flatten curves, strip clip paths)</span>
              </label>
            </fieldset>`
                : ""
            }
          </div>
        </div>
      </div>
      <footer>
        <button class="btn primary" type="button" data-act="download">Download ${format.toUpperCase()}</button>
        <button class="btn" type="button" data-act="close">Close</button>
      </footer>
    `;
    // The whole dialog is optimized vector export: badge its title (#1120).
    const vectorTitle = modal.querySelector<HTMLElement>("header > span");
    if (vectorTitle) markPaidFeature(vectorTitle, "optimized-vector-export");

    const field = <T extends HTMLElement>(name: string) =>
      modal.querySelector<T>(`[data-field="${name}"]`)!;
    const checked = (name: string) =>
      field<HTMLInputElement>(name)?.checked === true;

    modal.addEventListener("click", (ev) => {
      if (ev.target === modal) {
        this.close();
        return;
      }
      const btn = (ev.target as HTMLElement).closest(
        "[data-act]",
      ) as HTMLButtonElement | null;
      if (!btn) return;
      if (btn.dataset.act === "close") {
        this.close();
        return;
      }
      if (btn.dataset.act !== "download") return;
      try {
        const settings: VectorExportSettings = {
          content: {
            pattern: checked("pattern"),
            constructionLines: checked("construction"),
            stitching: field<HTMLSelectElement>("stitching")
              .value as StitchExportMode,
          },
          operations: {
            pattern: field<HTMLSelectElement>("opPattern")
              .value as ExportOperation,
            constructionLines: field<HTMLSelectElement>("opConstruction")
              .value as ExportOperation,
            stitching: field<HTMLSelectElement>("opStitching")
              .value as ExportOperation,
          },
          laser: {
            cutColor: field<HTMLInputElement>("cutColor").value,
            insideCutColor: field<HTMLInputElement>("insideCutColor").value,
            cutWidthMm:
              parseFloat(field<HTMLInputElement>("cutWidth").value) ||
              DEFAULT_VECTOR_EXPORT_SETTINGS.laser.cutWidthMm,
            scoreColor: field<HTMLInputElement>("scoreColor").value,
            scoreWidthMm:
              parseFloat(field<HTMLInputElement>("scoreWidth").value) ||
              DEFAULT_VECTOR_EXPORT_SETTINGS.laser.scoreWidthMm,
            engraveColor: field<HTMLInputElement>("engraveColor").value,
          },
          calibration: {
            metric: checked("calMetric"),
            imperial: checked("calImperial"),
          },
        };
        const project = this.ctx.history.state;
        const lib = this.ctx.library;
        if (format === "svg") {
          downloadSvgOptimized(project, lib, settings, checked("cricut"));
        } else {
          downloadDxf(project, lib, { vector: settings });
        }
        this.close();
      } catch (e) {
        alert("Export failed: " + (e as Error).message);
      }
    });

    this.host.appendChild(modal);
    this.dialogEl = modal;
    modal.addEventListener("close", () => this.teardown());
    modal.showModal();
  }

  close() {
    this.dialogEl?.close();
  }

  private teardown() {
    if (this.dialogEl) {
      this.dialogEl.remove();
      this.dialogEl = null;
    }
  }
}
